import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, NavLink, useLocation } from 'react-router-dom';

import ContactListPage    from './pages/ContactListPage';
import ContactDetailPage  from './pages/ContactDetailPage';
import DashboardPage      from './pages/DashboardPage';
import MyProfilePage      from './pages/MyProfilePage';
import EventsListPage     from './pages/EventsListPage';
import EventDetailPage    from './pages/EventDetailPage';
import EventFormPage      from './pages/EventFormPage';
import DealsListPage      from './pages/DealsListPage';
import DealDetailPage     from './pages/DealDetailPage';
import DealFormPage       from './pages/DealFormPage';
import BulkUploadPage        from './pages/BulkUploadPage';
import TeamManagementPage    from './pages/TeamManagementPage';

import { getTeamMembers, setMockUser, getMockUser, getMe } from './utils/api';
import './styles/global.css';

const CAT_DOT = {
  lp_cogp:         'var(--av-lp)',
  lender:          'var(--av-lender)',
  broker:          'var(--av-broker)',
  partner_service: 'var(--av-partner)',
  seller_operator: '#5a9e9e',
};

function Dot({ color }) {
  return (
    <span style={{
      display: 'inline-block', width: 7, height: 7,
      borderRadius: '50%', background: color,
      marginLeft: 6, flexShrink: 0,
    }} />
  );
}

function AppContent() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [teamMembers, setTeamMembers]   = useState([]);
  const [currentUser, setCurrentUser]   = useState(getMockUser());
  const [meData,      setMeData]        = useState(null);
  const location = useLocation();

  // Fetch /api/me on mount to get real role and permissions
  useEffect(() => {
    getMe().then(setMeData).catch(console.error);
  }, []);

  useEffect(() => { setSidebarOpen(false); }, [location]);

  useEffect(() => {
    getTeamMembers().then(r => setTeamMembers(r.data || r)).catch(console.error);
  }, []);

  const handleUserSwitch = (e) => {
    setMockUser(e.target.value);
    setCurrentUser(e.target.value);
    window.location.reload();
  };

  return (
    <div className="app-layout">
      <div className={`sidebar-overlay ${sidebarOpen ? 'open' : ''}`} onClick={() => setSidebarOpen(false)} />

      <aside className={`app-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-brand">
          <h1>Relationship Hub</h1>
          <span>Family Office CRM</span>
        </div>

        <nav className="sidebar-nav">
          <div className="sidebar-section-label">Overview</div>
          <NavLink to="/" end>Dashboard</NavLink>
          <NavLink to="/profile">My Profile</NavLink>

          <div className="sidebar-section-label">Contacts</div>
          <NavLink to="/contacts" end>All Contacts</NavLink>
          {[
            { to: '/contacts?category=lp_cogp',        label: 'LP / Co-GP',  color: CAT_DOT.lp_cogp },
            { to: '/contacts?category=lender',         label: 'Lenders',     color: CAT_DOT.lender },
            { to: '/contacts?category=broker',         label: 'Brokers',     color: CAT_DOT.broker },
            { to: '/contacts?category=partner_service',label: 'Partners / Service', color: CAT_DOT.partner_service },
            { to: '/contacts?category=seller_operator', label: 'Sellers / Operators', color: CAT_DOT.seller_operator },
          ].map(({ to, label, color }) => (
            <NavLink
              key={to}
              to={to}
              className={({ isActive }) => {
                // isActive only checks pathname — also check query string
                const url = new URL(window.location.href);
                const match = url.pathname === '/contacts' && url.search === '?' + to.split('?')[1];
                return match ? 'active' : '';
              }}
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            >
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <Dot color={color} />{label}
              </span>
            </NavLink>
          ))}

          <div className="sidebar-section-label">Events</div>
          <NavLink to="/events">Networking Events</NavLink>

          <div className="sidebar-section-label">Deals</div>
          <NavLink to="/deals">Deal Pipeline</NavLink>
          {meData?.role === 'admin' && (
            <>
              <div className="sidebar-section-label">Admin</div>
              <NavLink to="/admin/team">Team management</NavLink>
            </>
          )}
        </nav>

        <div className="dev-user-switcher">
          <label style={{ display: 'block', marginBottom: '4px', fontSize: '0.7rem', color: 'var(--color-text-muted)' }}>
            DEV MODE — Acting as:
          </label>
          <select value={currentUser} onChange={handleUserSwitch}>
            {teamMembers.map(m => (
              <option key={m.id} value={m.email}>{m.display_name} ({m.role})</option>
            ))}
          </select>
        </div>
      </aside>

      <div className="app-main">
        <header className="app-header">
          <button className="mobile-menu-btn" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 5h14M3 10h14M3 15h14" />
            </svg>
          </button>
          <span style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>Relationship Hub</span>
        </header>

        <div className="app-content">
          <Routes>
            {/* Overview */}
            <Route path="/"        element={<DashboardPage />} />
            <Route path="/profile" element={<MyProfilePage meData={meData} />} />
          <Route path="/contacts/bulk-upload" element={<BulkUploadPage />} />
          <Route path="/admin/team" element={<TeamManagementPage meData={meData} />} />

            {/* Contacts */}
            <Route path="/contacts"     element={<ContactListPage />} />
            <Route path="/contacts/:id" element={<ContactDetailPage />} />

            {/* Events */}
            <Route path="/events"          element={<EventsListPage />} />
            <Route path="/events/new"      element={<EventFormPage />} />
            <Route path="/events/:id"      element={<EventDetailPage />} />
            <Route path="/events/:id/edit" element={<EventFormPage />} />

            {/* Deals */}
            <Route path="/deals"          element={<DealsListPage />} />
            <Route path="/deals/new"      element={<DealFormPage />} />
            <Route path="/deals/:id"      element={<DealDetailPage />} />
            <Route path="/deals/:id/edit" element={<DealFormPage />} />
          </Routes>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return <BrowserRouter basename="/relationship-hub"><AppContent /></BrowserRouter>;
}
